import React from 'react';
import { useParams } from 'next/navigation';
import { useFormat } from 'helpers/hooks/useFormat';
import { Product } from 'types/entity/product';
import CurrentRefinements from './components/current-refinements';
import DesktopFacets from './components/desktop-facets';
import SortFacet from './components/facets/sort';
import List from './components/list';
import MobileFacets from './components/mobile-facets';
import SearchHeader from './components/search-header';
import { useProductList } from './context';
import PlpBackground from './images/plp-background';

export interface ProductListProps {
  products: Product[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    limit?: number;
  };
}

const ProductList: React.FC<ProductListProps> = ({ products, pagination }) => {
  const { searchQuery, categories } = useProductList();
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { slug } = useParams();
  const currentCategory = categories.filter((cat) => cat.slug === slug[slug.length - 1]);
  const hasResults = products.length > 0;

  return (
    <div className="min-h-[80vh] md:mt-60 lg:py-15 xl:mt-0">
      {!hasResults && (
        <div className="absolute bottom-0 right-0 z-0">
          <PlpBackground />
        </div>
      )}
      <div className="relative flex flex-col gap-15 p-15 md:gap-30 lg:flex-row lg:px-30 xl:mx-auto xl:max-w-[1200px] xl:px-0">
        {hasResults && (
          <aside className="w-full lg:w-1/5 lg:pt-26">
            <div>
              {searchQuery ? (
                <div>
                  <h1 className="px-12 text-18 font-bold text-secondary-black lg:px-0">
                    {formatProductMessage({
                      id: 'search.results.for',
                      defaultMessage: 'Search results for',
                    })}{' '}
                    <span className="font-medium">“{searchQuery}”</span>
                  </h1>
                </div>
              ) : (
                <div>
                  <h1 className="px-12 text-18 font-bold text-secondary-black md:text-26 lg:px-0 lg:text-28">
                    {currentCategory?.[0]?.name}
                  </h1>
                </div>
              )}
            </div>
            <DesktopFacets pagination={pagination} />
          </aside>
        )}
        {hasResults && <MobileFacets />}
        <div className={`w-full ${hasResults && 'lg:w-4/5'}`}>
          {hasResults && (
            <div className="hidden w-full items-center justify-end lg:flex">
              <SortFacet />
            </div>
          )}
          {hasResults && <CurrentRefinements />}
          <List products={products} pagination={pagination} currentCategory={currentCategory} />
        </div>
      </div>
    </div>
  );
};

export default ProductList;
